/**
 * lib/contactRouting.js (v2.87.x)
 * --------------------------------
 * Centralized helper for resolving the right contact CTA on the public
 * property detail page. Implements the "Public Detail Page Contact
 * Protection + HouseEntry Lead Routing" P0 spec.
 *
 * RULE MATRIX
 *   • Anonymous (no user)       → REQUIRES LOGIN — both WA + Call open the auth gate.
 *   • Logged-in client          → Routes to HouseEntry official WA + phone with a
 *                                  rich pre-filled message containing the listing's
 *                                  Inventory ID + URL.
 *   • Logged-in dealer/builder
 *       — owns the listing      → keeps existing direct contact (their own number).
 *       — does NOT own listing  → same routing as client (via HouseEntry).
 *   • admin / team_member       → No CTA on public detail page (they use the admin
 *                                  console for lead management — return null).
 *
 * IMPORTANT
 *   • Backend ALSO strips owner contact for non-admin/non-team viewers — so
 *     even if a future caller forgets to use this helper, dealer_phone will
 *     be empty in the API response. This helper is the UX layer; the
 *     backend strip in `strip_for_role()` is the security layer.
 *   • Inventory ID is mandatory in the WA message because many listings can
 *     look identical (e.g. multiple "50 Sq Yd 2BHK Dwarka Mor" rows). The
 *     ID + canonical URL together uniquely identify the inventory unit.
 */

const INTERNAL_ROLES = new Set(["admin", "team_member"]);

// Strip everything except digits. Always returns a 10-digit (or longer) string
// suitable for use after a country prefix.
const digitsOnly = (v) => String(v || "").replace(/\D/g, "");

// Build a wa.me link from a raw number (10 digits or already with country code).
const waUrl = (rawNumber, text) => {
  const d = digitsOnly(rawNumber);
  if (!d) return null;
  // If admin entered "9812345678" (10 digits) we prepend 91.
  // If admin entered "919812345678" (12+ digits) we leave as-is.
  const intl = d.length === 10 ? `91${d}` : d;
  return `https://wa.me/${intl}?text=${encodeURIComponent(text || "")}`;
};

const telUrl = (rawNumber) => {
  const d = digitsOnly(rawNumber);
  if (!d) return null;
  const intl = d.length === 10 ? `+91${d}` : `+${d}`;
  return `tel:${intl}`;
};

// Format the property price for the WA message. Mirrors the brand share
// formatter but stays standalone so this helper doesn't take a JSX-dep.
const formatPropertyPrice = (p) => {
  const intent = String(p?.listing_intent || "sale").toLowerCase();
  const num = (v) => (v != null && !Number.isNaN(Number(v)) ? Number(v) : 0);
  const inr = (n) => {
    if (!n) return "";
    if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
    if (n >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
    return `₹${n.toLocaleString("en-IN")}`;
  };
  if (intent === "rent") {
    const m = num(p?.monthly_rent || p?.rent_details?.monthly_rent);
    return m ? `${inr(m)}/month (Rent)` : "";
  }
  if (intent === "lease") {
    const m = num(p?.monthly_rent);
    return m ? `${inr(m)}/month (Lease)` : "";
  }
  const m = num(p?.price);
  return m ? `${inr(m)} (Sale)` : "";
};

const formatSize = (p) => {
  const UNIT_LABEL = { sqft: "Sq Ft", sqyd: "Sq Yd", sqm: "Sq M", acre: "Acre" };
  const n = Math.round(Number(p?.size_sqft || 0));
  if (!n) return "";
  const u = String(p?.size_unit || "sqyd").toLowerCase();
  return `${n} ${UNIT_LABEL[u] || UNIT_LABEL.sqyd}`;
};

const formatArea = (p) => {
  const parts = [p?.sub_area, p?.main_area || p?.area].filter(Boolean);
  return parts.join(", ");
};

/**
 * Build the HouseEntry-routed pre-filled WhatsApp message. Inventory ID +
 * URL are mandatory; other fields are skipped if missing.
 *
 * @param {object} p - the property dict (from /api/properties/:id)
 * @param {string} currentUrl - window.location.href at click time
 * @returns {string} - the message body (NOT URL-encoded; encode at link build)
 */
export function buildHouseEntryEnquiryMessage(p, currentUrl) {
  const lines = ["Hello HouseEntry, I am interested in this property."];
  lines.push("");
  // Mandatory.
  lines.push(`Inventory ID: ${p?.id || "(missing)"}`);
  if (p?.title) lines.push(`Title: ${p.title}`);
  if (p?.property_type) lines.push(`Type: ${p.property_type}`);
  const config = p?.bhk || p?.configuration;
  if (config && config !== "Not Specified") lines.push(`Configuration: ${config}`);
  const size = formatSize(p);
  if (size) lines.push(`Size: ${size}`);
  const area = formatArea(p);
  if (area) lines.push(`Area: ${area}`);
  const price = formatPropertyPrice(p);
  if (price) lines.push(`Price: ${price}`);
  // Mandatory.
  lines.push(`Listing URL: ${currentUrl || "(missing)"}`);
  lines.push("");
  lines.push("Please share details and availability.");
  return lines.join("\n");
}

/**
 * Resolve the official HouseEntry contact numbers from the admin-editable
 * config (`/api/config` → `content.footer.phone` + `content.footer.social.whatsapp`).
 * Falls back to safe defaults so a missing config doesn't break the CTA.
 *
 * @param {object} content - useConfig().content
 * @returns {{ phone: string, whatsapp: string }}
 */
export function resolveHouseEntryContacts(content) {
  const footer = (content || {}).footer || {};
  const phone = footer.phone || "+91 7027722709";
  const whatsapp = (footer.social || {}).whatsapp || footer.phone || "";
  return { phone, whatsapp };
}

/**
 * The main contact-routing helper. Returns a normalized action descriptor
 * that the UI uses to render the right CTA.
 *
 * @param {object} property        - property dict from /api/properties/:id
 * @param {object|null} currentUser - the logged-in user (or null for anon)
 * @param {object} content         - useConfig().content (footer carries numbers)
 * @param {string} currentUrl      - window.location.href at the moment of render
 * @returns {object} action descriptor:
 *   {
 *     mode: "login_required" | "houseentry" | "owner_direct" | "internal_hidden",
 *     whatsappHref: string|null,
 *     telHref: string|null,
 *     buttonLabel: string,           // overrides default "WhatsApp"/"Call"
 *     callLabel: string,
 *     requiresLogin: boolean,
 *     reason: string,                // human-friendly debug hint
 *   }
 */
export function getPublicContactAction(property, currentUser, content, currentUrl) {
  const role = currentUser?.role || null;

  // 1. Internal operators don't see customer CTAs on the public detail page.
  if (INTERNAL_ROLES.has(role)) {
    return {
      mode: "internal_hidden",
      whatsappHref: null,
      telHref: null,
      buttonLabel: "",
      callLabel: "",
      requiresLogin: false,
      reason: "admin/team — use admin console for lead management",
    };
  }

  // 2. Owner (dealer/builder viewing their own listing) keeps the direct
  //    contact line — they're managing their own inventory.
  const isOwnerDealerOrBuilder =
    (role === "dealer" || role === "builder") &&
    currentUser?.id &&
    (property?.submitted_by_id === currentUser.id ||
      property?.created_by === currentUser.id);
  if (isOwnerDealerOrBuilder) {
    const phoneRaw = property?.dealer_phone || property?.contact || "";
    return {
      mode: "owner_direct",
      whatsappHref: waUrl(phoneRaw, `Re: ${property?.title || "your listing"}`),
      telHref: telUrl(phoneRaw),
      buttonLabel: "WhatsApp",
      callLabel: "Call",
      requiresLogin: false,
      reason: "viewer owns the listing",
    };
  }

  // 3. Anonymous user → login wall.
  if (!currentUser) {
    return {
      mode: "login_required",
      whatsappHref: null,
      telHref: null,
      buttonLabel: "Login to Contact HouseEntry",
      callLabel: "Login to Contact HouseEntry",
      requiresLogin: true,
      reason: "no auth — protect owner inventory",
    };
  }

  // 4. Logged-in client (or non-owner dealer/builder) → route via HouseEntry.
  const { phone, whatsapp } = resolveHouseEntryContacts(content);
  const message = buildHouseEntryEnquiryMessage(property, currentUrl);
  return {
    mode: "houseentry",
    whatsappHref: waUrl(whatsapp || phone, message),
    telHref: telUrl(phone),
    buttonLabel: "WhatsApp HouseEntry",
    callLabel: "Call HouseEntry",
    requiresLogin: false,
    reason: "client/non-owner — central lead routing",
  };
}

// Default export for parity with other lib/ helpers.
export default getPublicContactAction;
